import type {
    ExpenseRecord,
    ExpenseFilterCriteria,
    ExpenseCategoryEntry,
} from "../types/costTrackingTypes";

// ----- Expense Filtering (Req 2.4, 2.5, 2.7) -----

/**
 * Filters expense records by the given criteria. All specified criteria must match
 * (AND logic). Supports search query (across vendorName, workDescription, invoiceNumber),
 * date range, equipment ID, vendor ID, and category name.
 *
 * Validates: Requirements 2.4, 2.5, 2.7
 */
export function filterExpenses(
    expenses: ExpenseRecord[],
    criteria: ExpenseFilterCriteria,
): ExpenseRecord[] {
    return expenses.filter((expense) => {
        if (criteria.searchQuery) {
            const q = criteria.searchQuery.toLowerCase();
            const matchesSearch = [
                expense.vendorName,
                expense.workDescription,
                expense.invoiceNumber,
            ].some((field) => field?.toLowerCase().includes(q));
            if (!matchesSearch) return false;
        }
        if (criteria.dateRange) {
            const d = new Date(expense.dateOfService + (expense.dateOfService.includes('T') ? '' : 'T12:00:00'));
            if (d < criteria.dateRange.start || d > criteria.dateRange.end)
                return false;
        }
        if (criteria.equipmentId && expense.unitId !== criteria.equipmentId)
            return false;
        if (criteria.vendorId && expense.vendorId !== criteria.vendorId)
            return false;
        if (criteria.categoryName) {
            const categories: ExpenseCategoryEntry[] = JSON.parse(
                expense.expenseCategories || "[]",
            );
            if (
                !categories.some(
                    (c) => c.categoryName === criteria.categoryName,
                )
            )
                return false;
        }
        return true;
    });
}

/**
 * Quick filter for category tabs — returns expenses that have the given category
 * in their expenseCategories JSON array.
 *
 * Validates: Requirements 2.4, 2.5
 */
export function filterExpensesByCategory(
    expenses: ExpenseRecord[],
    categoryName: string,
): ExpenseRecord[] {
    return expenses.filter((expense) => {
        const categories: ExpenseCategoryEntry[] = JSON.parse(
            expense.expenseCategories || "[]",
        );
        return categories.some((c) => c.categoryName === categoryName);
    });
}
